import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useSitesStore } from '@/stores/sites';
import { Card, CardHeader, Button, Input, Modal } from '@/components/UI';

export function SiteSettings() {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const {
    currentSite,
    fetchSite,
    updateSite,
    deleteSite,
    regenerateTrackingCode,
    isLoading,
  } = useSitesStore();

  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (siteId) {
      fetchSite(siteId);
    }
  }, [siteId]);

  useEffect(() => {
    if (currentSite) {
      setName(currentSite.name);
      setDomain(currentSite.domain);
    }
  }, [currentSite]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!siteId) return;

    setIsSaving(true);
    try {
      await updateSite(siteId, { name, domain });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!siteId) return;

    setIsDeleting(true);
    try {
      await deleteSite(siteId);
      navigate('/dashboard');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRegenerateCode = async () => {
    if (!siteId) return;
    await regenerateTrackingCode(siteId);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!currentSite && !isLoading) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900">Site not found</h2>
        <Link to="/dashboard" className="mt-4 inline-block">
          <Button variant="secondary">Back to sites</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <div className="flex items-center gap-2">
          <Link to="/dashboard" className="text-gray-500 hover:text-gray-700">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">Site Settings</h1>
        </div>
        <p className="text-gray-600">{currentSite?.domain}</p>
      </div>

      {/* General Settings */}
      <Card>
        <CardHeader title="General" description="Update your site's basic information" />
        <form onSubmit={handleSave} className="space-y-4">
          <Input
            label="Site Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <Input
            label="Domain"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            required
          />
          <div className="flex justify-end">
            <Button type="submit" isLoading={isSaving}>
              Save Changes
            </Button>
          </div>
        </form>
      </Card>

      {/* Tracking Code */}
      <Card>
        <CardHeader
          title="Tracking Code"
          description="Add this code to your website to start tracking"
        />
        <div className="space-y-4">
          <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
            <code className="text-sm text-green-400 whitespace-pre">
              {currentSite?.tracking_snippet ||
                `<script defer data-site="${currentSite?.tracking_code}" src="https://statsmatic.io/tracking.js"></script>`}
            </code>
          </div>
          <div className="flex gap-3">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => copyToClipboard(currentSite?.tracking_snippet || '')}
            >
              {copied ? 'Copied!' : 'Copy Code'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRegenerateCode}
            >
              Regenerate Code
            </Button>
          </div>
          <p className="text-sm text-gray-500">
            Place this code in the <code className="bg-gray-100 px-1 rounded">&lt;head&gt;</code> section of your HTML.
          </p>
        </div>
      </Card>

      {/* Danger Zone */}
      <Card className="border-red-200">
        <CardHeader
          title="Danger Zone"
          description="Irreversible actions"
        />
        <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
          <div>
            <h4 className="font-medium text-red-900">Delete this site</h4>
            <p className="text-sm text-red-700">
              Permanently delete this site and all its analytics data.
            </p>
          </div>
          <Button
            variant="danger"
            onClick={() => setIsDeleteModalOpen(true)}
          >
            Delete Site
          </Button>
        </div>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete Site"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Are you sure you want to delete <strong>{currentSite?.name}</strong>?
            This action cannot be undone and will permanently delete all analytics data.
          </p>
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => setIsDeleteModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              isLoading={isDeleting}
            >
              Delete Site
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default SiteSettings;
